import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, ExternalLink } from 'lucide-react'
import { SITE, NAV_LINKS } from '../content/siteContent'

export default function Navbar() {
  const [open, setOpen]       = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location              = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close mobile menu on route change
  useEffect(() => { setOpen(false) }, [location])

  const isActive = (href: string) => {
    if (href === '/#home') return location.pathname === '/'
    return location.pathname.startsWith(href.replace('/#', '/'))
  }

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-soft' : 'bg-white/80 backdrop-blur-sm'
      }`}
      role="banner"
    >
      <div className="section-container">
        <nav className="flex items-center justify-between h-16 md:h-20" aria-label="Main navigation">

          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group focus:outline-none focus:ring-2 focus:ring-brand-sky rounded-lg p-1"
            aria-label={`${SITE.name} — Home`}
          >
            <div className="w-10 h-10 bg-brand-navy rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-brand-blue transition-colors duration-200">
              <span className="text-white font-display font-bold text-lg leading-none">GP</span>
            </div>
            <div className="hidden sm:block leading-tight">
              <p className="text-brand-navy font-display font-bold text-base leading-tight">
                Grace Pointe
              </p>
              <p className="text-brand-blue text-xs font-round font-semibold tracking-wide">
                Child Development Center
              </p>
            </div>
          </Link>

          {/* Desktop nav */}
          <ul className="hidden lg:flex items-center gap-1" role="list">
            {NAV_LINKS.map((link) => (
              <li key={link.label}>
                <Link
                  to={link.href}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-sky ${
                    isActive(link.href)
                      ? 'bg-brand-cream text-brand-navy'
                      : 'text-slate-600 hover:text-brand-navy hover:bg-brand-cream'
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={SITE.procare.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:text-brand-navy transition-colors focus:outline-none focus:ring-2 focus:ring-brand-sky rounded-lg px-2 py-1"
            >
              Parent Portal
              <ExternalLink size={14} aria-hidden="true" />
            </a>
            <Link to="/#contact" className="btn-primary text-sm">
              Schedule a Tour
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 rounded-lg text-brand-navy hover:bg-brand-cream transition-colors focus:outline-none focus:ring-2 focus:ring-brand-sky"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>
      </div>

      {/* Mobile menu */}
      <div
        id="mobile-menu"
        className={`lg:hidden overflow-hidden transition-all duration-300 ${open ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
        aria-hidden={!open}
      >
        <div className="bg-white border-t border-slate-100 shadow-lift px-4 py-4 space-y-1">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className={`block px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                isActive(link.href)
                  ? 'bg-brand-cream text-brand-navy'
                  : 'text-slate-600 hover:bg-brand-cream hover:text-brand-navy'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-2 flex flex-col gap-2">
            <a
              href={SITE.procare.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 btn-secondary text-sm"
            >
              Parent Portal <ExternalLink size={14} />
            </a>
            <Link to="/#contact" className="btn-primary text-sm text-center">
              Schedule a Tour
            </Link>
          </div>
        </div>
      </div>
    </header>
  )
}
